import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, ChevronUp, X } from "lucide-react";

const CommonDropdown = ({
  label,
  name,
  options,
  required = false,
  register,
  errors,
  defaultValue = "",
  value = undefined, // Current form value from watch()
  search = false,
  readOnly = false,
  labelClass = "font-bold text-base",
  secondLabel,
}) => {
  // Use value prop if provided (from watch), otherwise fall back to defaultValue
  const currentValue = value !== undefined ? value : defaultValue;
  
  // Find the default option based on current value
  const defaultOption = options?.find((option) => option.value === currentValue);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(defaultOption || null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Register the field with react-hook-form - let yup handle validation
  const {
    onChange,
    onBlur,
    name: fieldName,
    ref,
  } = register(name);

  // Update selected option when value or defaultValue changes
  useEffect(() => {
    const option = options?.find((option) => option.value === currentValue);
    if (option) {
      setSelectedOption(option);
    } else if (currentValue === "" || currentValue === null || currentValue === undefined) {
      // Clear selection if value is empty
      setSelectedOption(null);
    }
  }, [currentValue, options]);

  // Filter options based on search term (only when search is enabled)
  const filteredOptions = search
    ? options?.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens (if search is enabled)
  useEffect(() => {
    if (isOpen && search && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, search]);

  // Handle option selection
  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
    setSearchTerm("");

    // Trigger react-hook-form onChange event
    const event = {
      target: {
        name,
        value: option.value,
      },
    };
    onChange(event);
    // Also trigger validation
    onBlur(event);
  };

  // Handle dropdown toggle
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && search && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  // Clear selection
  const clearSelection = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    setSearchTerm("");

    // Trigger react-hook-form onChange event with empty string
    const event = {
      target: {
        name,
        value: "",
      },
    };
    onChange(event);
    // Trigger validation
    onBlur(event);
  };

  // Initialize form value on component mount
  useEffect(() => {
    if (currentValue && defaultOption && !selectedOption) {
      // Trigger initial onChange to set the form value
      const event = {
        target: {
          name,
          value: currentValue,
        },
      };
      onChange(event);
    }
  }, [currentValue, defaultOption, onChange, name, selectedOption]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className={`${labelClass}`} htmlFor={name}>
          {label}
          {required && <span className="text-red-500 text-xs ml-0.5">*</span>}
        </label>
        {secondLabel}
      </div>

      {/* Custom dropdown UI - same design for both search and non-search */}
      <div className="relative" ref={dropdownRef}>
        <div
          className={`flex items-center justify-between border rounded-lg px-4 py-3 cursor-pointer transition-all ${
            isOpen ? "ring-2 ring-blue-200 border-blue-300" : ""
          } ${
            errors?.[name]
              ? "border-red-500 focus:border-red-300 ring-red-200"
              : "border-gray-300 hover:border-gray-400"
          }`}
          onClick={toggleDropdown}
        >
          <div className="flex-1 flex items-center">
            {!readOnly && isOpen && search ? (
              <div className="flex items-center w-full">
                <Search size={16} className="text-gray-400 mr-2" />
                <input
                  type="text"
                  className="w-full outline-none bg-transparent"
                  placeholder="Search..."
                  readOnly={readOnly}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  ref={searchInputRef}
                />
              </div>
            ) : (
              <span className={`${!selectedOption && "text-gray-500"}`}>
                {selectedOption ? selectedOption.label : "Select an option"}
              </span>
            )}
          </div>
          {!readOnly && (
            <div className="flex items-center">
              {selectedOption && !isOpen && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="p-1 hover:bg-gray-100 rounded-full mr-1"
                >
                  <X size={16} className="text-gray-500" />
                </button>
              )}
              {isOpen ? (
                <ChevronUp size={18} className="text-gray-500" />
              ) : (
                <ChevronDown size={18} className="text-gray-500" />
              )}
            </div>
          )}
        </div>

        {/* Options dropdown */}
        {!readOnly && isOpen && (
          <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                    selectedOption?.value === option.value
                      ? "bg-blue-50 text-blue-600"
                      : ""
                  }`}
                  onClick={() => handleSelectOption(option)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500 italic">
                No options found
              </div>
            )}
          </div>
        )}

        {/* Hidden input for react-hook-form */}
        <input
          type="hidden"
          name={fieldName}
          ref={ref}
          onBlur={onBlur}
          value={selectedOption?.value ?? ""}
          onChange={onChange}
        />
      </div>

      {errors && errors[name] && (
        <p className="text-red-500 text-xs mt-1">{errors[name]?.message}</p>
      )}
    </div>
  );
};

export default CommonDropdown;
